import store, { dispatch } from '../redux/store';
import { activeMenu } from '../utils/menu';
import { appId } from '../constants/app';
import { appInitiated, firstPageShowEventCompleted } from '../redux/slice/appSlice';
import { authChannel } from '../broadcast-channels/authBroadcast';
import { CustomEventType, MenuEvent } from '../types/event';
import { getStoredState, REHYDRATE } from 'redux-persist';
import { onAuthInit, setAuthIdentity } from '../redux/slice/authSlice';
import { persistedConfigs } from '../redux/slice';
import { PersistedStateKeys } from '../types/store';
import { setupMockInterceptor } from '../apis';
import { setUserLocationFromCoordinatesEpicAction } from '@app/redux/slice/locationSlice';
import { captureSalesContactPrefill } from '@app/utils/payment-account/sales-contact-prefill';

declare global {
  interface Window {
    pageshowEventCount: number;
    isRentalWebComponentsInitialized: boolean;
  }
}

let initialized: boolean = false;

const initializeApplication = () => {
  if (initialized) {
    return;
  }

  initialized = true;

  // Before any sign-in redirect can drop the query string.
  captureSalesContactPrefill(window.location.pathname, window.location.search);

  // Listen for local category events and set user location if not already set
  // Example use: This is currently used in local category pages to set
  // the user location to the mapped local location
  document.addEventListener(CustomEventType.LOCAL_CATEGORY, (e: Event) => {
    const event = e as CustomEvent<{ latitude: number; longitude: number }>;
    const currentLocation = store.getState().location.userLocation;
    if (!currentLocation) {
      dispatch(
        setUserLocationFromCoordinatesEpicAction({
          latitude: event.detail.latitude,
          longitude: event.detail.longitude,
        }),
      );
    }
  });

  // This helps other components (eg: webflow) know that 
  // rental-web-components has been initialized
  window.isRentalWebComponentsInitialized = true;
  const event = new CustomEvent(CustomEventType.RENTAL_WEB_COMPONENTS_INITIALIZED);
  document.dispatchEvent(event);

  window.addEventListener('pageshow', () => {
    if (store.getState().app.firstPageShowEventCompleted || window['pageshowEventCount'] > 1) {
      Object.keys(PersistedStateKeys).forEach((e1) => {
        getStoredState(persistedConfigs[e1 as PersistedStateKeys]).then((e2) => {
          store.dispatch({
            type: REHYDRATE,
            key: e1,
            payload: e2,
          });
        });
      });
      store.dispatch(onAuthInit());
      store.dispatch(appInitiated());
      if (store.getState().app.firstPageShowEventCompleted) {
        store.dispatch(firstPageShowEventCompleted());
      }
    } else {
      store.dispatch(firstPageShowEventCompleted());
    }
  });

  if (window['pageshowEventCount'] === 1 && !store.getState().app.firstPageShowEventCompleted) {
    store.dispatch(firstPageShowEventCompleted());
  }

  document.addEventListener(CustomEventType.MENU, (e: Event) => {
    const value = (e as unknown as MenuEvent).detail;
    if (value) {
      activeMenu.next(value);
    }
  });

  authChannel.onmessage = (message) => {
    if (appId !== message.appId) {
      dispatch(
        setAuthIdentity({
          identity: message.payload.identity,
          loginStatus: message.payload.loginStatus,
          isOwner: message.payload.isOwner,
        }),
      );
    }
  };

  setupMockInterceptor();
  // Google Maps is loaded on demand by its consumers (autocomplete, geocoding,
  // checkout maps) — see utils/googlePlaces. Eagerly loading it here injected
  // the Maps script on every page, including ones with no location feature
  // (/careers, /privacy-policy, /terms), which was the bulk of the RUM
  // "could not load" noise (sc-558707).
};

export default initializeApplication;
